import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Clock, CheckCircle2, AlertTriangle, User, TrendingUp, Calendar, FileText, Flame } from "lucide-react";
import { useCleanerPerformance, CleanerPerformance } from "@/hooks/useCleanerPerformance";
import { format } from "date-fns";
import { formatScheduledDate } from "@/lib/dateUtils";

interface CleanerPerformanceTabProps {
  propertyId: string;
}

// Helper to get completion percentage color class
const getCompletionColorClass = (percentage: number | null): string => {
  if (percentage === null) return "";
  if (percentage >= 97) return "bg-green-100 text-green-700";
  if (percentage >= 90) return "bg-yellow-100 text-yellow-700";
  return "bg-red-100 text-red-700";
};

// Helper to check if duration is slow (> 50% above average)
const isDurationSlow = (duration: number | null, propertyAvg: number | null): boolean => {
  if (duration === null || propertyAvg === null) return false;
  return duration > propertyAvg * 1.5;
};

const CleanerPerformanceTab = ({ propertyId }: CleanerPerformanceTabProps) => {
  const { data, isLoading } = useCleanerPerformance(propertyId);
  const [selectedCleaner, setSelectedCleaner] = useState<CleanerPerformance | null>(null);

  // Use empty arrays/objects as placeholders to avoid loading state
  const cleaners = (data?.cleaners && Array.isArray(data.cleaners)) ? data.cleaners : [];
  const propertyAverage = data?.propertyAverage || null;

  // Only show empty state if not loading and no data
  if (!isLoading && cleaners.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center max-w-md mx-auto">
          <User className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground font-medium">No cleaner performance data yet.</p>
          <p className="text-sm text-muted-foreground mt-2">
            This tab shows metrics only for tasks that were <strong>assigned to a cleaner</strong> and marked completed. If you completed tasks yourself (as owner) or assigned them to a team member instead of a cleaner, those won&apos;t appear here.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            To see performance: assign a <strong>cleaner</strong> to a cleaning task, then have that cleaner complete the task. After at least one such task is completed, metrics will show here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Cleaner Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="md:hidden space-y-2">
            {propertyAverage && (
              <div className="rounded-lg border p-3 space-y-1 text-sm bg-muted/30">
                <div className="font-medium italic text-muted-foreground">Property Average</div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Avg duration</span>
                  <span>
                    {propertyAverage.avg_duration_minutes !== null
                      ? `${propertyAverage.avg_duration_minutes} min`
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Avg quality</span>
                  <span>
                    {propertyAverage.avg_completion_percentage !== null
                      ? `${propertyAverage.avg_completion_percentage}%`
                      : "—"}
                  </span>
                </div>
              </div>
            )}
            {cleaners.map((cleaner) => (
              <button
                key={cleaner.cleaner_id}
                type="button"
                onClick={() => setSelectedCleaner(cleaner)}
                className="w-full rounded-lg border p-3 space-y-2 text-sm text-left hover:bg-muted/50 transition-colors"
              >
                <div className="font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground shrink-0" />
                  {cleaner.cleaner_name}
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Cleans</span>
                  <Badge variant="secondary">{cleaner.total_cleans_completed}</Badge>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Avg duration</span>
                  <span>
                    {cleaner.avg_duration_minutes !== null
                      ? `${cleaner.avg_duration_minutes} min`
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Avg quality</span>
                  <span>
                    {cleaner.avg_completion_percentage !== null
                      ? `${cleaner.avg_completion_percentage}%`
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Last clean</span>
                  <span>
                    {cleaner.last_clean_date
                      ? format(new Date(cleaner.last_clean_date), "MMM d, yyyy")
                      : "—"}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cleaner</TableHead>
                <TableHead className="text-center">Total Cleans</TableHead>
                <TableHead className="text-center">Avg Duration</TableHead>
                <TableHead className="text-center">Avg Quality</TableHead>
                <TableHead className="text-center">Issue Rate</TableHead>
                <TableHead className="text-center">Last Clean</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Property Average Row */}
              {propertyAverage && (
                <TableRow className="bg-muted/30 font-medium">
                  <TableCell>
                    <span className="text-muted-foreground italic">Property Average</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-muted-foreground">—</span>
                  </TableCell>
                  <TableCell className="text-center">
                    {propertyAverage.avg_duration_minutes !== null 
                      ? `${propertyAverage.avg_duration_minutes} min` 
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {propertyAverage.avg_completion_percentage !== null ? (
                      <Badge 
                        variant="secondary"
                        className={getCompletionColorClass(propertyAverage.avg_completion_percentage)}
                      >
                        {propertyAverage.avg_completion_percentage}%
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {propertyAverage.issue_rate_per_10 !== null ? (
                      <span className={propertyAverage.issue_rate_per_10 > 2 ? "text-red-600 font-medium" : ""}>
                        {propertyAverage.issue_rate_per_10} per 10
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-muted-foreground">—</span>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}

              {/* Cleaner Rows */}
              {cleaners.map((cleaner) => (
                <TableRow 
                  key={cleaner.cleaner_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedCleaner(cleaner)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      {cleaner.cleaner_name}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{cleaner.total_cleans_completed}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {cleaner.avg_duration_minutes !== null ? (
                      <span className={
                        isDurationSlow(cleaner.avg_duration_minutes, propertyAverage?.avg_duration_minutes ?? null)
                          ? "text-orange-600 font-medium"
                          : ""
                      }>
                        {cleaner.avg_duration_minutes} min
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {cleaner.avg_completion_percentage !== null ? (
                      <Badge 
                        variant="secondary"
                        className={getCompletionColorClass(cleaner.avg_completion_percentage)}
                      >
                        {cleaner.avg_completion_percentage}%
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {cleaner.issue_rate_per_10 !== null ? (
                      <span className={cleaner.issue_rate_per_10 > 2 ? "text-red-600 font-medium" : ""}>
                        {cleaner.issue_rate_per_10} per 10
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {cleaner.last_clean_date ? (
                      format(new Date(cleaner.last_clean_date), "MMM d, yyyy")
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm">
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Cleaner Detail Drawer */}
      <Sheet open={!!selectedCleaner} onOpenChange={() => setSelectedCleaner(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedCleaner && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {selectedCleaner.cleaner_name}
                </SheetTitle>
              </SheetHeader>

              {/* Summary Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <CheckCircle2 className="w-5 h-5 mx-auto text-green-600 mb-1" />
                  <p className="text-lg font-semibold">{selectedCleaner.total_cleans_completed}</p>
                  <p className="text-xs text-muted-foreground">Total Cleans</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <Clock className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                  <p className={`text-lg font-semibold ${
                    isDurationSlow(selectedCleaner.avg_duration_minutes, propertyAverage?.avg_duration_minutes ?? null)
                      ? "text-orange-600"
                      : ""
                  }`}>
                    {selectedCleaner.avg_duration_minutes !== null 
                      ? `${selectedCleaner.avg_duration_minutes} min` 
                      : "N/A"}
                  </p>
                  <p className="text-xs text-muted-foreground">Avg Duration</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <TrendingUp className="w-5 h-5 mx-auto text-purple-600 mb-1" />
                  <p className={`text-lg font-semibold ${
                    selectedCleaner.avg_completion_percentage !== null
                      ? selectedCleaner.avg_completion_percentage < 90
                        ? "text-red-600"
                        : selectedCleaner.avg_completion_percentage < 97
                          ? "text-yellow-600"
                          : "text-green-600"
                      : ""
                  }`}>
                    {selectedCleaner.avg_completion_percentage !== null 
                      ? `${selectedCleaner.avg_completion_percentage}%` 
                      : "N/A"}
                  </p>
                  <p className="text-xs text-muted-foreground">Avg Quality</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <AlertTriangle className="w-5 h-5 mx-auto text-orange-600 mb-1" />
                  <p className={`text-lg font-semibold ${
                    selectedCleaner.issue_rate_per_10 !== null && selectedCleaner.issue_rate_per_10 > 2
                      ? "text-red-600"
                      : ""
                  }`}>
                    {selectedCleaner.issue_rate_per_10 !== null 
                      ? selectedCleaner.issue_rate_per_10 
                      : "N/A"}
                  </p>
                  <p className="text-xs text-muted-foreground">Issues per 10</p>
                </div>
              </div>

              {/* Recent Cleans */}
              <div className="mt-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Recent Cleans
                </h3>
                <div className="space-y-2">
                  {selectedCleaner.tasks.slice(0, 15).map((task) => (
                    <div 
                      key={task.task_id}
                      className="p-3 border rounded-lg text-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">
                          {formatScheduledDate(task.scheduled_date, "MMM d, yyyy")}
                        </span>
                        {task.completed_at ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            Completed
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                            Pending
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-muted-foreground">
                        {task.checklist_name && (
                          <div className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            <span className="truncate">{task.checklist_name}</span>
                          </div>
                        )}
                        {task.duration_minutes !== null && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{task.duration_minutes} min</span>
                            {task.extra_dirty && (
                              <Flame className="w-3 h-3 text-orange-500" />
                            )}
                          </div>
                        )}
                        {task.completion_percentage !== null && (
                          <div className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            <span className={
                              task.completion_percentage < 90 ? "text-red-600" :
                              task.completion_percentage < 97 ? "text-yellow-600" : "text-green-600"
                            }>
                              {task.completion_percentage}%
                            </span>
                          </div>
                        )}
                        {task.issue_count > 0 && (
                          <div className="flex items-center gap-1 text-orange-600">
                            <AlertTriangle className="w-3 h-3" />
                            <span>{task.issue_count} issue{task.issue_count > 1 ? "s" : ""}</span>
                          </div>
                        )}
                        {task.extra_dirty && (
                          <div className="flex items-center gap-1 text-orange-500">
                            <Flame className="w-3 h-3" />
                            <span>Extra dirty</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {selectedCleaner.tasks.length > 15 && (
                    <p className="text-xs text-muted-foreground text-center pt-2">
                      Showing 15 of {selectedCleaner.tasks.length} tasks
                    </p>
                  )}
                  
                  {selectedCleaner.tasks.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">
                      No cleaning tasks found.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default CleanerPerformanceTab;